import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AppNavigation } from "../components/AppNavigation";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  XCircle,
  Crown,
  ArrowRight,
  Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SubscriptionSuccessPage = ({ user, logout, token }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error, pending
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 10;
  const pollInterval = 2000;

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async () => {
    if (pollAttempts >= maxPollAttempts) {
      setStatus("pending");
      toast.info("El pago está siendo procesado. Recibirás una confirmación pronto.");
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/subscription/checkout/status/${sessionId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPaymentDetails(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        toast.success("¡Pago completado exitosamente!");
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        toast.error("La sesión de pago ha expirado");
        return;
      }

      // Continue polling
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      
      // If it's an auth error, don't retry
      if (error.response?.status === 401) {
        setStatus("error");
        return;
      }
      
      // Retry on other errors
      setPollAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-6" />
              <h2 className="text-2xl font-bold mb-2">Verificando tu pago...</h2>
              <p className="text-muted-foreground">
                Por favor espera mientras confirmamos tu suscripción
              </p>
            </CardContent>
          </Card>
        );

      case "success":
        return (
          <Card className="max-w-md mx-auto border-primary/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">¡Bienvenido a StratIA Pro!</h2>
              <p className="text-muted-foreground mb-6">
                Tu suscripción ha sido activada correctamente. Ya puedes disfrutar de todas las funcionalidades premium.
              </p>
              
              <div className="bg-secondary/50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-primary mb-2">
                  <Crown className="w-5 h-5" />
                  <span className="font-semibold">Plan Pro Activo</span>
                </div>
                {paymentDetails && (
                  <p className="text-sm text-muted-foreground">
                    Total pagado: €{(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/dashboard")}
                  data-testid="go-to-dashboard-btn"
                >
                  Ir al Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/composer")}
                >
                  Crear tu primer post
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      case "pending":
        return (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto bg-accent/10 rounded-full flex items-center justify-center mb-6">
                <Clock className="w-12 h-12 text-accent" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Pago en Proceso</h2>
              <p className="text-muted-foreground mb-6">
                Tu pago está siendo procesado. Esto puede tomar unos minutos. Recibirás un email de confirmación cuando esté listo.
              </p>
              
              <Button 
                className="w-full" 
                onClick={() => navigate("/dashboard")}
                data-testid="go-to-dashboard-pending-btn"
              >
                Ir al Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        );

      case "error":
        return (
          <Card className="max-w-md mx-auto border-destructive/30">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-12 h-12 text-destructive" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Error en el Pago</h2>
              <p className="text-muted-foreground mb-6">
                Hubo un problema al procesar tu pago. Por favor intenta de nuevo o contacta con soporte si el problema persiste.
              </p>
              
              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/subscription")}
                  data-testid="retry-payment-btn"
                >
                  Intentar de nuevo
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/dashboard")}
                >
                  Volver al Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="subscription-success-page">
      <AppNavigation user={user} logout={logout} />
      
      <main className="lg:pl-64">
        <div className="p-6 min-h-screen flex items-center justify-center">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};
